import { gql } from '@apollo/client';

export const CREATE_INVOICE = gql`
  mutation CreateInvoice($input: CreateInvoiceInput!) {
    createInvoice(createInvoiceInput: $input) {
      id
      shortId
      bookingId
      amount
      balance
      type
      status
      description
      stripePaymentIntentId
      stripePaymentMethodId
      stripeRefundId
      originalInvoiceId
      receiptUrl
      receiptId
      dueDate
      createdAt
      processedAt
      # New refund-related fields
      refundAmount
      refundStatus
      refundInitiatedAt
      refundCompletedAt
      note
      hostApprovedRefund
      hostRefundReviewedAt
      breakdown {
        # Base fees
        carPricePerTrip
        insurancePerTrip
        taxRate
        tripFee
        tax

        # Additional fees
        tolls
        tickets
        lateFees
        mileageExcess
        oneWayFee
        youngsterFee
        fuelCharge
        cleaningFee
        damageCharges

        # Discounts
        discounts
        promoCode
        promoAmount

        # Totals
        subtotal
        totalPrice

        # Payment related
        depositAmount
        depositRefund
        totalRefund
        baseRefund
        insuranceRefund
        oneWayFeeRefund
        cancellationPenalty
        taxRefund

        # Cancellation related
        penaltyAmount

        # Notes
        notes
      }
      booking {
        id
        status
        startAt
        endAt
        receipt {
          last4digitOfCard
        }
      }
    }
  }
`;

export const CREATE_REFUND_INVOICE = gql`
  mutation CreateRefundInvoice($bookingId: String!) {
    createRefundInvoice(bookingId: $bookingId) {
      id
      bookingId
      amount
      balance
      type
      status
      description
      stripePaymentIntentId
      stripePaymentMethodId
      stripeRefundId
      originalInvoiceId
      receiptUrl
      receiptId
      dueDate
      createdAt
      processedAt
      # New refund-related fields
      refundAmount
      refundStatus
      refundInitiatedAt
      refundCompletedAt
      note
      hostApprovedRefund
      hostRefundReviewedAt
      breakdown {
        # Base fees
        carPricePerTrip
        insurancePerTrip
        taxRate
        tripFee
        tax

        # Additional fees
        tolls
        tickets
        lateFees
        mileageExcess
        oneWayFee
        youngsterFee
        fuelCharge
        cleaningFee
        damageCharges

        # Discounts
        discounts
        promoCode
        promoAmount

        # Totals
        subtotal
        totalPrice

        # Payment related
        depositAmount
        depositRefund
        totalRefund
        baseRefund
        insuranceRefund
        oneWayFeeRefund
        cancellationPenalty
        taxRefund

        # Cancellation related
        penaltyAmount

        # Notes
        notes
      }
      booking {
        id
        status
        startAt
        endAt
        receipt {
          last4digitOfCard
        }
      }
    }
  }
`;

export const REMOVE_INVOICE = gql`
  mutation RemoveInvoice($id: ID!) {
    removeInvoice(id: $id)
  }
`;

export const GET_INVOICES_BY_REFUND_STATUS = gql`
  query GetInvoicesByRefundStatus($filter: InvoiceFilterInput!) {
    invoicesByRefundStatus(filter: $filter) {
      items {
        id
        amount
        createdAt
        hostRefundReviewedAt
        refundStatus
        type
        status
        stripePaymentIntentId
        stripeRefundId
        originalInvoiceId
        note
        booking {
          id
          startAt
          endAt
          cancellationReason
        }
        originalBookingState {
          startAt
          endAt
          totalPrice
          carPricePerTrip
          insuranceByTrip
          pickupLocationId
          returnLocationId
        }
        newBookingState {
          startAt
          endAt
          totalPrice
          carPricePerTrip
          insuranceByTrip
          pickupLocationId
          returnLocationId
        }
        requester {
          id
          displayName
          email
        }
        host {
          id
          displayName
          email
        }
      }
      total
    }
  }
`;

export const GET_HOST_INVOICES_BY_REFUND_STATUS = gql`
  query GetHostInvoicesByRefundStatus($filter: InvoiceFilterInput!) {
    invoicesByRefundStatusForHost(filter: $filter) {
      items {
        id
        amount
        createdAt
        hostRefundReviewedAt
        refundStatus
        type
        status
        stripePaymentIntentId
        stripeRefundId
        originalInvoiceId
        note
        booking {
          id
          startAt
          endAt
          cancellationReason
        }
        originalBookingState {
          startAt
          endAt
          totalPrice
          carPricePerTrip
          insuranceByTrip
          pickupLocationId
          returnLocationId
        }
        newBookingState {
          startAt
          endAt
          totalPrice
          carPricePerTrip
          insuranceByTrip
          pickupLocationId
          returnLocationId
        }
        requester {
          id
          displayName
          email
        }
        host {
          id
          displayName
          email
        }
      }
      total
    }
  }
`;

export const CREATE_SHORTEN_REFUND_INVOICE = gql`
  mutation CreateShortenRefundInvoice($input: CreatePartialRefundInput!) {
    createShortenRefundInvoice(input: $input) {
      id
      amount
      type
      status
      note
      breakdown {
        baseRefund
        taxRefund
        insuranceRefund
        penaltyAmount
      }
      createdAt
    }
  }
`;

export const GET_INVOICE_BY_ID = gql`
  query GetInvoiceById($id: ID!) {
    invoice(id: $id) {
      id
      shortId
      amount
      createdAt
      refundInitiatedAt
      hostRefundReviewedAt
      refundStatus
      type
      status
      stripePaymentIntentId
      stripeRefundId
      originalInvoiceId
      note
      booking {
        id
        startAt
        endAt
        cancellationReason
      }
      originalBookingState {
        startAt
        endAt
        totalPrice
        carPricePerTrip
        insuranceByTrip
        pickupLocationId
        returnLocationId
      }
      newBookingState {
        startAt
        endAt
        totalPrice
        carPricePerTrip
        insuranceByTrip
        pickupLocationId
        returnLocationId
      }
      requester {
        id
        displayName
        email
      }
      host {
        id
        displayName
        email
      }
    }
  }
`;

export const REFRESH_INVOICE_REFUND_STATUS = gql`
  mutation RefreshInvoiceRefundStatus($invoiceId: ID!) {
    refreshInvoiceRefundStatus(invoiceId: $invoiceId) {
      id
      refundStatus
      refundCompletedAt
      hostRefundReviewedAt
    }
  }
`;

export const CREATE_REFUND_FOR_INVOICE = gql`
  mutation CreateRefundForInvoice($invoiceId: ID!, $note: String) {
    createRefundForInvoice(invoiceId: $invoiceId, note: $note) {
      id
      refundStatus
      refundInitiatedAt
      stripeRefundId
      note
    }
  }
`;
